import { AppGame } from "@core-types/game.enum";
import type { AppRegion } from "@core-types/region.enum";
import { RegionStrategyFactory } from "@helpers/regions/RegionStrategyFactory";
import UrlHelper from "@helpers/url.helper";
import useAppLanguage from "@hooks/language/useAppLanguage";
import { useAppNavigate } from "@hooks/language/useAppNavigate";
import useRegionStore from "@store/region.store";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router";

export const useAppRegion = () => {
  const { lang } = useAppLanguage();
  const { pathname } = useLocation();
  const { navigate } = useAppNavigate();

  const { getRegion, setRegion } = useRegionStore();
  const storedRegion = getRegion();

  const game = useMemo(() => {
    if (pathname.includes("valorant")) {
      return AppGame.VALORANT;
    }
    return AppGame.LOL;
  }, [pathname]);

  const strategy = useMemo(
    () => RegionStrategyFactory.getStrategy(game),
    [game],
  );

  const regionInfo = useMemo(
    () => strategy.getInfoByCode(storedRegion),
    [strategy, storedRegion],
  );
  const region = regionInfo.code;

  // Sync store if sanitized region differs from stored region (context switch)
  useEffect(() => {
    if (region !== storedRegion) {
      setRegion(region);
    }
  }, [region, storedRegion, setRegion]);

  const regionAndLanguageUrlBuilder = useCallback(
    (...uri: string[]) => {
      return UrlHelper.getI18nRegionsUrl({
        language: lang.currentLanguage,
        region,
        uri,
      });
    },
    [lang.currentLanguage, region],
  );

  const regionUrlBuilder = useCallback(
    (...uri: string[]) => {
      return UrlHelper.getI18nRegionsUrl({
        region,
        uri,
      });
    },
    [region],
  );

  const changeRegionUrlBuilder = (newRegion: AppRegion, ...uri: string[]) =>
    strategy.getRegionHref(uri.join("/"), newRegion);

  const changeRegion = (newRegion: AppRegion, callback?: () => void) => {
    setRegion(newRegion);
    const href = changeRegionUrlBuilder(newRegion, pathname);

    navigate(href);

    if (callback) {
      callback();
    }
  };

  const allRegions = useMemo(() => strategy.getAllInfo(), [strategy]);

  return {
    currentRegion: region,
    allRegions,
    /**
     * Changes the current region (modify region store and navigate to the new region URL)
     *
     * Can pass a callback to be executed after the region change
     * */
    changeRegion,
    regionAndLanguageUrlBuilder,
    regionUrlBuilder,
    changeRegionUrlBuilder,
  };
};
